import { z } from 'zod';
import { getExecEndpoint } from '~/api/service/getExecEndpoint';
import { ApiEndpointDef } from '~/api/types';
import { defaultServer } from '../servers/defaultServer';
import { BqResponse } from './types';

export const companiesHouseEndpoint = {
  server: defaultServer,
  path: 'companies-house',
  description: 'Companies House operations',
};

export const endpointCompaniesHouseExec = <
  TResponse extends ApiEndpointDef,
>() => getExecEndpoint<TResponse>(companiesHouseEndpoint);

export const endpointCompaniesHouseSearchGET = async (query: string) => {
  const exec =
    endpointCompaniesHouseExec<
      CompaniesHouseEndpointSearchDef<GetCompaniesHouseSearchResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra: `/search?q=${encodeURIComponent(query)}`,
  });

  if (!response) {
    throw new Error('Could not search for portfolio company');
  }

  if (response.success === 0) {
    throw new Error('Could not search for portfolio company');
  }

  return response.data;
};

export const companiesHouseSearchGetSchema = z.object({
  search: z.string().min(1, 'Search query is required'),
});

export const companyAddressSchema = z.object({
  addressLine1: z.string(),
  addressLine2: z.string().optional(),
  locality: z.string().optional(),
  region: z.string().optional(),
  postalCode: z.string(),
  country: z.string().optional(),
});

export const companySchema = z.object({
  companyName: z.string(),
  companyNumber: z.string(),
  company_status: z.string(),
  registeredOfficeAddress: companyAddressSchema,
  link: z.string(),
});

export const companiesHouseResponseSchema = z.object({
  topHit: companySchema,
  items: z.array(companySchema),
  count: z.number(),
});

type CompaniesHouseSearchResponse = z.infer<
  typeof companiesHouseResponseSchema
>;

export type GetCompaniesHouseSearchResponse = BqResponse<{
  data: CompaniesHouseSearchResponse;
}>;

export interface CompaniesHouseEndpointSearchDef<TResponse> {
  endpoint: typeof companiesHouseEndpoint;
  methods: {
    GET: {
      response: TResponse;
    };
  };
}

export const endpointCompaniesHouseShareholdersGET = async (
  companyNumber: string,
) => {
  const exec =
    endpointCompaniesHouseExec<
      CompaniesHouseEndpointShareholdersDef<GetCompaniesHouseShareholdersResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra: `/${companyNumber}/initial-shareholders`,
  });

  if (!response) {
    throw new Error('Could not search for portfolio company');
  }

  if (response.success === 0) {
    throw new Error('Could not search for portfolio company');
  }

  return response.data;
};

export const companiesHouseShareholdersResponseSchema = z.array(
  z.object({
    name: z.string(),
    shareClass: z.string(),
    shareAmount: z.string(),
    nominalValue: z.string(),
    currency: z.string(),
  }),
);

type CompaniesHouseShareholdersResponse = z.infer<
  typeof companiesHouseShareholdersResponseSchema
>;

export type GetCompaniesHouseShareholdersResponse = BqResponse<{
  data: CompaniesHouseShareholdersResponse;
}>;

export interface CompaniesHouseEndpointShareholdersDef<TResponse> {
  endpoint: typeof companiesHouseEndpoint;
  methods: {
    GET: {
      response: TResponse;
    };
  };
}
